/**
 * The AI assistants a parent might use, and what connecting each to Babia
 * takes. One source for the in-app page (/assistant) and the copied prompt;
 * the guide an assistant reads (src/client/connect.md, served at /connect.md)
 * says the same things in its own words, and tests/assistants.test.ts keeps
 * the two in step. Check the steps against each assistant's help pages when
 * this changes: they move faster than this app does.
 */
import { amountText, BOTTLE_FIRSTS, type BottleUnit } from "@babia/checks";

/**
 * The public endpoint, not this page's origin: the app also answers on its
 * Railway fallback domain (and localhost in development), but assistants'
 * tokens are bound to `${BETTER_AUTH_URL}/mcp`, which is this in production.
 */
export const MCP_URL = "https://babia.app/mcp";

/** The guide an assistant reads when the parent pastes the prompt. */
export const CONNECT_GUIDE_URL = "https://babia.app/connect.md";

/**
 * The one message a parent pastes into their assistant. It works on its own
 * (it names the connector URL, for an assistant that can't open links) and
 * asks the assistant to read the guide for the exact steps. Plain words, so a
 * parent can see what they're pasting.
 */
export const CONNECT_PROMPT = `Please help me connect Babia, my baby log, to you as a custom connector (its URL is ${MCP_URL}). Read ${CONNECT_GUIDE_URL} for the steps, then walk me through them one at a time.`;

export type AssistantId =
  | "chatgpt"
  | "gemini"
  | "claude"
  | "copilot"
  | "meta"
  | "grok"
  | "perplexity"
  | "other";

export interface Assistant {
  id: AssistantId;
  name: string;
  /** Whether a parent can connect Babia to it today. */
  connects: boolean;
  /** The page that opens it, to paste the message into. */
  url?: string;
  /** Its own page for adding a connector with Babia's name and URL filled in. */
  addUrl?: string;
  /** The plan or device it takes, said plainly. */
  needs?: string;
  /** The steps by hand, for a parent who'd rather not be walked through them. */
  steps: string[];
  /** For one that can't connect: why, in a sentence. */
  why?: string;
}

/** The last step everywhere: Babia's own sign-in and consent. */
const SIGN_IN = "Sign in with the Google or Facebook account you use for Babia, then tap Allow.";

/**
 * Claude first, as the guide has it: it is the quickest to connect (a link
 * fills in its form) and works on its free plan. Then the rest, in the order
 * a parent is likely to look for them. Checked against each assistant's help
 * pages on 2026-10-03 (see connect.md for the sources).
 */
export const ASSISTANTS: Assistant[] = [
  {
    id: "claude",
    name: "Claude",
    connects: true,
    url: "https://claude.ai/new",
    addUrl: `https://claude.ai/customize/connectors?modal=add-custom-connector&connectorName=Babia&connectorUrl=${encodeURIComponent(MCP_URL)}`,
    needs:
      "Works on the free plan, which allows one custom connector. Add it on claude.ai in a web browser or the desktop app; then it works in the Claude phone app too.",
    steps: [
      "On claude.ai, open Customize → Connectors.",
      "Click + Add, then Add custom connector.",
      "Name it Babia, paste the address below, and click Add.",
      "Click Connect next to Babia.",
      SIGN_IN,
    ],
  },
  {
    id: "chatgpt",
    name: "ChatGPT",
    connects: true,
    url: "https://chatgpt.com/",
    needs:
      "Takes a paid plan (Plus, Pro, Business, Enterprise, or Edu; on a work or school plan, an admin may need to allow it), set up on chatgpt.com in a web browser; a computer is easiest. ChatGPT calls this Developer mode and shows a warning, which is expected, and may ask you to confirm entries before it saves them.",
    steps: [
      "On chatgpt.com, open Settings → Security and login, and turn on Developer mode.",
      "Open Plugins (chatgpt.com/plugins), click +, and create a developer-mode app.",
      "Name it Babia, paste the address below, choose OAuth for sign-in, and create it.",
      SIGN_IN,
      "In a new chat, open the + menu, choose Developer mode, and switch on Babia.",
    ],
  },
  {
    id: "gemini",
    name: "Gemini",
    connects: true,
    url: "https://gemini.google.com/",
    needs:
      "For personal Google accounts in the US, 18 or older, in English, with Keep Activity on. Add it on gemini.google.com in a web browser; then it works in the Gemini phone app too.",
    steps: [
      "On gemini.google.com, open Settings → Connected Apps.",
      "Under Custom apps, click Add a custom app.",
      "Paste the address below, click Next, and follow the steps.",
      SIGN_IN,
      "In a chat, type @ and pick Babia, or just say what happened.",
    ],
  },
  {
    id: "copilot",
    name: "Copilot",
    connects: false,
    url: "https://copilot.microsoft.com/",
    steps: [],
    why: "Microsoft’s Copilot app connects only to a few services of its own choosing, such as Outlook and Gmail. It can’t add others, like Babia, yet.",
  },
  {
    id: "meta",
    name: "Meta AI",
    connects: false,
    url: "https://www.meta.ai/",
    steps: [],
    why: "Meta AI (in its own app, or in WhatsApp, Instagram, and Facebook) can’t add connectors like Babia yet.",
  },
  {
    id: "grok",
    name: "Grok",
    connects: true,
    url: "https://grok.com/",
    needs: "Add it on grok.com in a web browser.",
    steps: [
      "Go to grok.com/connectors.",
      "Click New Connector, then Custom.",
      "Paste the address below and continue.",
      SIGN_IN,
    ],
  },
  {
    id: "perplexity",
    name: "Perplexity",
    connects: true,
    url: "https://www.perplexity.ai/",
    needs:
      "Takes a paid plan (Pro or Max), set up on perplexity.ai in a web browser or the desktop app.",
    steps: [
      "On perplexity.ai, open Settings → Connectors.",
      "Click + Custom connector, then Remote.",
      "Name it Babia, paste the address below, and add it.",
      SIGN_IN,
    ],
  },
  {
    id: "other",
    name: "Something else",
    connects: true,
    needs:
      "Works with any assistant that can add a custom connector by its address. Some call it a custom app, an integration, or a remote MCP server.",
    steps: [
      "In your assistant’s settings, find Connectors (or Apps, or Integrations).",
      "Add a custom connector named Babia, with the address below.",
      SIGN_IN,
    ],
  },
];

/**
 * What an allowed assistant can do, in the parent's words (mirrors the
 * packages/mcp tools). The consent screen asks with these, and Settings lists
 * them under each connected assistant.
 */
export const ABILITIES = [
  "Log bottles, diaper changes, naps, meds with their doses, weights, and notes (baths and more) for your babies",
  "Read your household's log, from any dates, to answer questions, including weights with their WHO percentiles and what looks forgotten",
  "Edit, move, or delete entries, for example to fix a mistake",
  "Send your feedback to the people who make Babia",
];

/**
 * What to call an assistant: the name it registered with, else the host of
 * its home page or of its client id (a Client ID Metadata Document's id is a
 * URL). A dynamic registration with neither is "An AI assistant".
 */
export function assistantName(client: {
  name?: string | null;
  uri?: string | null;
  clientId?: string | null;
}): string {
  const name = client.name?.trim();
  if (name) return name;
  for (const url of [client.uri, client.clientId]) {
    if (!url) continue;
    try {
      const { hostname } = new URL(url);
      if (hostname) return hostname;
    } catch {
      // Not a URL: a dynamically registered client's random id.
    }
  }
  return "An AI assistant";
}

/** The babies' names in a sentence: "Ada", "Ada and Bea", "Ada, Bea, and Cy". */
export function namesInWords(names: string[]): string {
  if (names.length <= 2) return names.join(" and ");
  return `${names.slice(0, -1).join(", ")}, and ${names.at(-1)}`;
}

/**
 * The example bottles in things to try saying, in the household's unit: the
 * third and second sizes that count as a first (4 oz and 3 oz; 120 ml and
 * 90 ml), so the examples are sizes the overview also names.
 */
export function exampleBottle(unit: BottleUnit): { big: string; small: string } {
  const sizes = BOTTLE_FIRSTS[unit];
  return { big: amountText(sizes[2]!, unit), small: amountText(sizes[1]!, unit) };
}

/**
 * Things to try saying once connected, in the household's own names and
 * bottle unit: logging both twins at once, a question, the forgotten-entry
 * check, and a fix.
 */
export function tryPhrases(names: string[], unit: BottleUnit): string[] {
  const [a, b] = names;
  const { big, small } = exampleBottle(unit);
  if (!a) return [`She had ${big} at 2am.`, "When did she last eat?", "Did we miss anything?"];
  if (!b)
    return [
      `${a} had ${big} at 2am.`,
      `When did ${a} last eat?`,
      `${a} fell asleep at 7:15.`,
      "Did we miss anything?",
    ];
  const both = names.length === 2 ? "both" : "all";
  return [
    `${names.length === 2 ? `${a} and ${b}` : "The babies"} ${both} had ${small} twenty minutes ago.`,
    `When did ${b} last eat?`,
    "Did we miss anything?",
    `That bottle was ${b}’s, not ${a}’s.`,
  ];
}
